import {
  Button,
  Dialog,
  FormField,
  Radio,
  RadioGroup,
  Textarea,
  Column,
  Row,
  Text,
  MultiSelect,
  Badge,
  Box,
  ButtonGroup,
  Checkbox,
} from "@hightouch/htui";
import { Controller } from "react-hook-form";
import { keyBy } from "lodash";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useUser } from "src/contexts/user-context";
import { switchWorkspace } from "src/utils/workspaces";
import { isAdminWithWritePrivilege } from "src/utils/user";
import { useUserGroupsForWorkspaceQuery } from "src/graphql";
import { useHightouchForm } from "src/components/form";
import { useEffect } from "react";
import { SupportAccessGrantRequiredAlert } from "src/components/support-access/support-access-grant-required-alert";
import { useImpersonationPermissions } from "src/hooks/use-impersonation-permissions";
import {
  useAdminEntitlementRequest,
  AdminEntitlementStatus,
} from "src/hooks/use-admin-entitlement-request";
import { EntitlementRequestAlert } from "src/components/admin-entitlements/entitlement-request-alert";

const DEFAULT_UPGRADE_REASONS = [
  "Troubleshooting an issue (per customer request)",
  "Troubleshooting an issue (proactively/internal request)",
  "Understanding usage to prepare for a customer call",
  "Learning about a feature",
];

const OTHER_UPGRADE_REASON = "Other";

type FormState = {
  impersonationType: "admin" | "viewer" | "readsupport" | "user_groups";
  userGroupIds: string[]; // only for user_groups
  upgradeReason: string;
  otherUpgradeReason: string;
  confirmMasterOverride: boolean;
};

const validationSchema = Yup.object().shape({
  impersonationType: Yup.string().required("Impersonation type is required"),
  userGroupIds: Yup.array().when("impersonationType", {
    is: "user_groups",
    then: Yup.array().min(1, "Select at least one user group"),
  }),
  upgradeReason: Yup.string().required("Upgrade reason is required"),
  otherUpgradeReason: Yup.string().when("upgradeReason", {
    is: OTHER_UPGRADE_REASON,
    then: Yup.string().required("Reason is required"),
  }),
  confirmMasterOverride: Yup.boolean(),
});

export const ImpersonationPermissionsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { user, workspace } = useUser();
  const { adminReadSupportEnabled } = useFlags();

  const { data: userGroups, isLoading: userGroupsLoading } =
    useUserGroupsForWorkspaceQuery(
      {
        workspaceId: workspace?.id ?? "",
      },
      {
        select: (data) =>
          // Defensive: a user_group_workspace row can have a null user_group;
          // filter those out before mapping.
          data.user_group_workspaces.filter((ugw) => Boolean(ugw.user_group)),
        enabled: Boolean(workspace?.organization),
      },
    );

  const {
    isPermitted: hasUpdatePermission,
    isLoading: updatePermissionLoading,
  } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_update" },
  });

  const hasWritePrivilege = isAdminWithWritePrivilege(user?.admin_type);

  const { control, watch, submit, setValue } = useHightouchForm<FormState>({
    defaultValues: {
      impersonationType: adminReadSupportEnabled
        ? "readsupport"
        : hasWritePrivilege
          ? "admin"
          : "viewer",
      userGroupIds: [],
      upgradeReason: DEFAULT_UPGRADE_REASONS[0],
      otherUpgradeReason: "",
      confirmMasterOverride: false,
    },
    success: "Reloading with new permissions",
    resolver: yupResolver(validationSchema),
    onSubmit: async (values) => {
      const requestWritePermission =
        values.impersonationType === "admin" ||
        values.impersonationType === "user_groups";

      const requestUserGroups =
        values.impersonationType === "user_groups"
          ? values.userGroupIds
          : undefined;

      const requestImpersonationType =
        values.impersonationType === "readsupport" ? "readsupport" : undefined;

      const upgradeReason =
        values.upgradeReason === OTHER_UPGRADE_REASON
          ? values.otherUpgradeReason
          : values.upgradeReason;

      const upgradeReasonDetailed = values.confirmMasterOverride
        ? `${upgradeReason} [MASTER OVERRIDE]`
        : upgradeReason;

      const workspaceId = user?.current_workspace_id;

      if (!workspaceId) return;

      await switchWorkspace(workspaceId, window.location.pathname, {
        requestWritePermission,
        requestUserGroups,
        requestImpersonationType,
        upgradeReason: upgradeReasonDetailed,
      });
    },
  });

  const impersonationType = watch("impersonationType");
  const upgradeReason = watch("upgradeReason");
  const confirmMasterOverride = watch("confirmMasterOverride");

  const userGroupsById = keyBy(userGroups ?? [], (ug) =>
    String(ug.user_group.id),
  );

  const {
    isFetchingGrant,
    isGrantRequired,
    isEntitlementRequired,
    satisfiedByGrant,
    refetchGrant,
    isMasterOverrideAllowed,
    disableSubmit,
  } = useImpersonationPermissions(impersonationType, confirmMasterOverride);

  const {
    requestState: entitlementRequestState,
    isLoading: entitlementRequestLoading,
    isAwaitingResolution: entitlementAwaitingResolution,
    submitRequest: submitEntitlementRequest,
    checkStatus: checkEntitlementStatus,
  } = useAdminEntitlementRequest();

  useEffect(() => {
    // Refetch the grant when the modal is opened to ensure we're not showing stale data
    if (isOpen && isGrantRequired) {
      refetchGrant();
    }
  }, [isOpen, refetchGrant, isGrantRequired]);

  useEffect(() => {
    // Only check entitlement request status if we have a pending request.
    // Don't poll if no request has been made yet.
    if (
      isOpen &&
      isEntitlementRequired &&
      entitlementRequestState.status === AdminEntitlementStatus.Pending
    ) {
      checkEntitlementStatus();
    }
  }, [
    isOpen,
    isEntitlementRequired,
    entitlementRequestState.status,
    checkEntitlementStatus,
  ]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    // Reset the master override checkbox when the modal opens or impersonation type changes to make sure
    // user always has to check the box again
    setValue("confirmMasterOverride", false);
  }, [impersonationType, isOpen, setValue]);

  const isLoading = userGroupsLoading || updatePermissionLoading;

  if (isLoading) {
    return null;
  }

  return (
    <Dialog
      width="lg"
      variant="form"
      isOpen={isOpen}
      onClose={onClose}
      title="Request impersonation permissions"
      actions={
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            isDisabled={
              disableSubmit ||
              (isEntitlementRequired &&
                entitlementRequestState.status !==
                  AdminEntitlementStatus.Approved)
            }
          >
            Submit
          </Button>
        </ButtonGroup>
      }
    >
      <Column gap={4}>
        {isGrantRequired && (
          <SupportAccessGrantRequiredAlert
            grant={satisfiedByGrant}
            workspaceSlug={workspace?.slug}
            isFetching={isFetchingGrant}
            additionalContent={
              isMasterOverrideAllowed && (
                <Column gap={4}>
                  <Text color="text.danger" fontWeight="semibold">
                    As an engineering admin, you have the ability to "break
                    glass" and access this workspace without waiting for org
                    admin approval. This option exists to help you respond
                    quickly during fires.
                  </Text>
                  <Text color="text.danger" fontWeight="semibold">
                    Use only when necessary. If you&apos;re sure you need
                    immediate access, check the box below. Otherwise, follow the
                    standard approval process.
                  </Text>
                  <Controller
                    control={control}
                    name="confirmMasterOverride"
                    render={({ field }) => (
                      <Checkbox
                        label="Break glass and bypass approval"
                        isChecked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                </Column>
              )
            }
          />
        )}
        <Column>
          <Text fontWeight="semibold">Current permissions</Text>
          <Box as="ul">
            <Box as="li">
              <Row alignItems="center" gap={1}>
                <Text>Workspace admin</Text>
                <Badge variant={hasUpdatePermission ? "warning" : undefined}>
                  {hasUpdatePermission ? "true" : "false"}
                </Badge>
              </Row>
            </Box>
            <Box as="li">User groups</Box>
            <Box as="ul">
              {user?.user_group_ids?.map((groupId) => {
                return (
                  <Box as="li" key={groupId}>
                    <Text>
                      {userGroupsById[groupId]?.user_group.name} ({groupId})
                    </Text>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Column>
        <Divider />

        <Column gap={4}>
          <Controller
            control={control}
            name="impersonationType"
            render={({ field }) => (
              <FormField label="Requested permissions">
                <RadioGroup value={field.value} onChange={field.onChange}>
                  {hasWritePrivilege && <Radio label="Admin" value="admin" />}
                  {adminReadSupportEnabled && (
                    <Radio
                      label="Debug Access"
                      value="readsupport"
                      description="Read-only with model preview access"
                    />
                  )}
                  <Radio label="Viewer" value="viewer" />
                  {hasWritePrivilege && (
                    <Radio label="Specific user groups" value="user_groups" />
                  )}
                </RadioGroup>
              </FormField>
            )}
          />

          {impersonationType === "user_groups" && (
            <Controller
              control={control}
              name="userGroupIds"
              render={({ field, fieldState }) => (
                <MultiSelect
                  placeholder="Select user groups"
                  isInvalid={Boolean(fieldState.error)}
                  options={
                    userGroups?.map((ug) => ({
                      label: `${ug.user_group.name} (${ug.user_group.id})`,
                      value: String(ug.user_group.id),
                    })) ?? []
                  }
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Column>

        {(impersonationType === "admin" ||
          impersonationType === "user_groups") && (
          <>
            <Divider />
            <Controller
              control={control}
              name="upgradeReason"
              render={({ field }) => (
                <FormField
                  description="Activity will be logged. Only view customer data (model preview, run debugger, etc.) if necessary and with customer permission."
                  label="Why do you need additional permissions?"
                >
                  <RadioGroup
                    mt={3}
                    value={field.value}
                    onChange={field.onChange}
                  >
                    {DEFAULT_UPGRADE_REASONS.map((option) => {
                      return (
                        <Radio key={option} label={option} value={option} />
                      );
                    })}
                    <Radio label="Other" value={OTHER_UPGRADE_REASON} />
                  </RadioGroup>
                </FormField>
              )}
            />
            {upgradeReason === OTHER_UPGRADE_REASON && (
              <Controller
                control={control}
                name="otherUpgradeReason"
                render={({ field, fieldState }) => (
                  <Textarea
                    isInvalid={Boolean(fieldState.error)}
                    autoFocus
                    width="100%"
                    rows={2}
                    placeholder="Enter reason…"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </>
        )}
      </Column>
      {isEntitlementRequired && (
        <EntitlementRequestAlert
          requestState={entitlementRequestState}
          isLoading={entitlementRequestLoading}
          isAwaitingResolution={entitlementAwaitingResolution}
          onRequestAccess={() => {
            const reason =
              upgradeReason === OTHER_UPGRADE_REASON
                ? watch("otherUpgradeReason")
                : upgradeReason;
            submitEntitlementRequest(reason || "Admin impersonation access");
          }}
          onRefreshStatus={checkEntitlementStatus}
        />
      )}
    </Dialog>
  );
};

const Divider = () => (
  <Box
    borderTopWidth="hairline"
    borderTopStyle="solid"
    borderColor="base.border"
  />
);
